import React from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { getActiveBrandConfig } from "../../../config/environment.config";
import { isVerifiedTrainer } from "../../../Utils/VerifiedTrainerAmounts";
import { DV2 } from "../theme";
import { CourseType, DeclarationStep, hasDeclarationInProgress, stepNumber } from "../types";
import { studentFullName } from "../format";
import { vocab } from "../vocabulary";
import { useDeclarationV2 } from "../DeclarationV2Context";
import { Badge, Button, Card, TextLink, useDv2Theme } from "../layout/Ui";

/**
 * Les quatre étapes de l'accueil, personnalisées par marque : élève/client,
 * cours/prestation, et Verified Trainer n'a pas de matière à choisir
 * (comme le titre de l'étape 3).
 */
const buildSteps = () => {
    const isVt = isVerifiedTrainer();
    const theClient = vocab.theClient();
    const theClientCap = `${theClient.charAt(0).toUpperCase()}${theClient.slice(1)}`;
    return [
        {
            n: "1",
            title: `Votre ${vocab.client()}`,
            text: `Choisissez-le dans votre liste, on vous montre où il en est ce mois-ci.`,
        },
        {
            n: "2",
            title: "Les dates",
            text: `Une date, ou plusieurs d'un coup grâce au calendrier, pour tous vos ${vocab.courses()} du mois.`,
        },
        isVt
            ? { n: "3", title: "Votre tarif", text: `Vous saisissez le prix horaire payé par ${vocab.theClient()}.` }
            : {
                n: "3",
                title: `${vocab.subjectLabel()} et tarif`,
                text: `Vous indiquez ${vocab.subjectLabelLower() === "matière" ? "la matière" : "la prestation"} et le prix horaire payé par ${vocab.theClient()}.`,
            },
        {
            n: "✓",
            title: "C'est déclaré",
            text: `C'est tout ! Vous pouvez consulter vos ${vocab.courses()} déclarés.`,
        },
    ];
};

/**
 * Accueil de la déclaration (maquettes 1j et 2j) : les quatre étapes en un
 * coup d'œil, puis le choix « Un seul cours » ou « Plusieurs cours ».
 * Un parcours commencé peut être repris là où il en était.
 */
const HomeStep = () => {
    const { teachr, state, update, reset, goTo } = useDeclarationV2();
    const { primary, text, fontFamily } = useDv2Theme();
    const navigate = useNavigate();
    const inProgress = hasDeclarationInProgress(state);
    const firstName = teachr?.firstName ?? "";

    const start = (courseType: CourseType) => {
        // Nouveau parcours : on repart d'un état vide, sauf si l'on reprend un
        // parcours en cours du même type (le choix se fait avec « Reprendre »).
        reset();
        update({ courseType, step: "student" });
        goTo("student");
    };

    const resumeStep: DeclarationStep = state.resumeStep ?? "student";
    const resume = () => {
        update({ step: resumeStep, resumeStep: null });
        goTo(resumeStep);
    };

    const stepLabel = (step: DeclarationStep) => {
        const n = stepNumber(step);
        return n ? `étape ${n} sur 4` : "";
    };

    const steps = buildSteps();

    return (
        <div style={{ fontFamily, color: DV2.text, }} className="w-full">
            <div className="mx-auto w-full max-w-5xl px-4 sm:px-6 py-6 lg:py-9 flex flex-col gap-7">
                <div className="flex flex-col gap-2">
                    <h1 style={{ fontSize: 28, fontWeight: 800, margin: 0, lineHeight: 1.15, color: text }} className="lg:text-[30px]">
                        Bonjour {firstName}, {vocab.aCourse()} à déclarer ?
                    </h1>
                    <p style={{ fontSize: 16, color: text, margin: 0, maxWidth: 680, lineHeight: 1.55, }}>
                        Quatre étapes, environ deux minutes. Vous pouvez vous arrêter quand vous voulez, votre saisie est conservée sur cet appareil.
                    </p>
                </div>

                {inProgress && state.student && (
                    <Card style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 12, borderColor: primary }}>
                        <div>
                            <div style={{ fontWeight: 800, color: text }}>Une déclaration est en cours</div>
                            <div style={{ fontSize: 13, color: text }}>
                                {state.courseType === "multiple" ? vocab.multipleCoursesTitle() : vocab.singleCourseTitle()} avec {studentFullName(state.student.child)}, {stepLabel(resumeStep)}
                                {state.updatedAt ? `, ${moment(state.updatedAt).locale("fr").fromNow()}` : ""}.
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <Button label="Reprendre" onClick={resume} size="sm" />
                            <Button label="Abandonner" variant="ghost" onClick={reset} size="sm" />
                        </div>
                    </Card>
                )}

                {/* Sur mobile, le déroulé tient sur une ligne : quatre pastilles reliées et leur titre, sans les textes. */}
                <section aria-label="Les étapes" style={{ background: primary, color: "#fff", borderRadius: 22 }} className="grid grid-cols-4 sm:grid-cols-2 lg:grid-cols-4 gap-1 sm:gap-6 px-3 py-4 sm:px-7 sm:py-6">
                    {steps.map((s, i) => (
                        <div key={s.n} className="flex flex-col items-center sm:items-start gap-1.5 sm:gap-2 min-w-0">
                            <div className="flex items-center gap-2 w-full justify-center sm:justify-start">
                                <div aria-hidden style={{ width: 34, height: 34, borderRadius: "50%", background: "#fff", color: primary, fontWeight: 800, fontSize: 15, display: "grid", placeItems: "center", flexShrink: 0 }}>{s.n}</div>
                                {i < steps.length - 1 && <div aria-hidden className="hidden lg:block" style={{ flex: 1, height: 2, background: "rgba(255,255,255,.3)" }} />}
                            </div>
                            <div className="text-center sm:text-left" style={{ fontWeight: 800, fontSize: 15, lineHeight: 1.25 }}>
                                <span className="sm:hidden" style={{ fontSize: 12 }}>{s.title}</span>
                                <span className="hidden sm:inline">{s.title}</span>
                            </div>
                            <div className="hidden sm:block" style={{ fontSize: 13, lineHeight: 1.45, opacity: 0.85 }}>{s.text}</div>
                        </div>
                    ))}
                </section>

                <section aria-label="Type de déclaration" className="flex flex-col gap-3">
                    <h2 style={{ fontSize: 17, fontWeight: 800, margin: 0, color: text }}>Par quoi on commence ?</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <button
                            type="button"
                            onClick={() => start("single")}
                            className="text-left flex flex-col gap-3 transition-shadow hover:shadow-md"
                            style={{ background: "#fff", border: `2px solid ${primary}`, borderRadius: 20, padding: 24, position: "relative", cursor: "pointer", fontFamily }}
                        >
                            <div style={{ position: "absolute", top: 16, right: 16 }}><Badge>Le plus courant</Badge></div>
                            <div aria-hidden style={{ width: 52, height: 52, borderRadius: 16, background: DV2.tint, color: primary, fontWeight: 800, fontSize: 22, display: "grid", placeItems: "center" }}>1</div>
                            <div>
                                <div style={{ fontSize: 19, fontWeight: 800, color: text }}>{vocab.singleCourseTitle()}</div>
                                <div style={{ fontSize: 14, color: text, lineHeight: 1.5, marginTop: 6 }}>
                                    {vocab.singleCourseTitle().charAt(0) === "U" && vocab.course() === "cours" ? "Un cours" : `${vocab.aCourse().charAt(0).toUpperCase()}${vocab.aCourse().slice(1)}`} à une date précise, {vocab.past()} ou à venir, dans le mois en cours. Idéal juste après la séance.
                                </div>
                            </div>
                            <div style={{ color: primary, fontWeight: 800, fontSize: 14, marginTop: "auto" }}>Déclarer {vocab.aCourse()} →</div>
                        </button>
                        <button
                            type="button"
                            onClick={() => start("multiple")}
                            className="text-left flex flex-col gap-3 transition-shadow hover:shadow-md"
                            style={{ background: "#fff", border: `1px solid ${DV2.border}`, borderRadius: 20, padding: 24, cursor: "pointer", fontFamily }}
                        >
                            <div aria-hidden style={{ width: 52, height: 52, borderRadius: 16, background: DV2.tint, color: primary, fontWeight: 800, fontSize: 22, display: "grid", placeItems: "center" }}>∞</div>
                            <div>
                                <div style={{ fontSize: 19, fontWeight: 800, color: text }}>{vocab.multipleSessionsTitle()}</div>
                                <div style={{ fontSize: 14, color: text, lineHeight: 1.5, marginTop: 6 }}>
                                    Tous les {vocab.courses()} du mois avec le même {vocab.client()}, par exemple chaque jeudi à 16h. Vous cochez les dates, on génère les séances.
                                </div>
                            </div>
                            <div style={{ color: primary, fontWeight: 800, fontSize: 14, marginTop: "auto" }}>Déclarer plusieurs {vocab.courses()} →</div>
                        </button>
                    </div>
                </section>

                <p style={{ fontSize: 13, color: text, lineHeight: 1.5, margin: 0 }}>
                    Seuls les {vocab.courses()} à domicile ouvrent droit à l'avance immédiate. Les {vocab.courses()} en ligne ne se déclarent pas ici.{" "}
                    <TextLink label="Comprendre l'avance immédiate" href={`${process.env.REACT_APP_SITE_URI_PROD}/comment-fonctionne-l-aici`} />
                </p>
            </div>
        </div>
    );
};

export default HomeStep;
